import React, { useState, useEffect } from 'react';
import api from '../utils/axios';
import { Card, CardHeader, CardTitle, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Switch } from '../components/ui/switch';
import { Label } from '../components/ui/label';
import { Input } from '../components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../components/ui/dialog';
import { toast } from 'sonner';
import {
  Users,
  User,
  UserCog,
  Globe,
  Plus,
  Edit,
  Trash2,
  Clock,
  Mail,
  Bell,
  RefreshCw,
  FileText,
  X
} from 'lucide-react';

const FREQUENCY_OPTIONS = [
  { value: 'hourly', label: 'Cada hora', icon: Clock },
  { value: 'daily', label: 'Diario', icon: Clock },
  { value: 'weekly', label: 'Semanal', icon: Clock },
  { value: 'none', label: 'Sin digest', icon: X }
];

const PRIORITY_OPTIONS = [
  { value: 'low', label: 'Baja (todas)' },
  { value: 'medium', label: 'Media' },
  { value: 'high', label: 'Alta' },
  { value: 'critical', label: 'Crítica (solo urgentes)' }
];

const DAY_OPTIONS = [
  { value: 'mon', label: 'Lunes' },
  { value: 'tue', label: 'Martes' },
  { value: 'wed', label: 'Miércoles' },
  { value: 'thu', label: 'Jueves' },
  { value: 'fri', label: 'Viernes' },
  { value: 'sat', label: 'Sábado' },
  { value: 'sun', label: 'Domingo' }
];

const TARGET_ICONS = {
  group: Users,
  user: User,
  role: UserCog,
  all: Globe
};

const TARGET_COLORS = {
  group: 'bg-blue-100 text-blue-700',
  user: 'bg-green-100 text-green-700',
  role: 'bg-purple-100 text-purple-700'
};

const NotificacionesSuscripciones = () => {
  const [subscriptions, setSubscriptions] = useState([]);
  const [availableTargets, setAvailableTargets] = useState({ groups: [], users: [], roles: [] });
  const [notificationTypes, setNotificationTypes] = useState({});
  const [loading, setLoading] = useState(true);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingSubscription, setEditingSubscription] = useState(null);
  
  // Form state - NUEVO: targets es una lista
  const [form, setForm] = useState({
    name: '',
    targets: [], // Lista de {type: "group"|"user"|"role", id: "..."}
    notification_types: ['*'],
    channels: ['in_app', 'digest'],
    digest_frequency: 'daily',
    digest_day: 'mon',
    digest_hour: 8,
    priority_threshold: 'high',
    active: true
  });
  
  // Selector temporal para agregar targets
  const [selectorType, setSelectorType] = useState('group');
  const [selectorId, setSelectorId] = useState('');

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      const [subsRes, targetsRes, typesRes] = await Promise.all([
        api.get('/notifications/v2/subscriptions'),
        api.get('/notifications/v2/subscriptions-targets'),
        api.get('/notifications/v2/types')
      ]);
      
      setSubscriptions(subsRes.data || []);
      setAvailableTargets(targetsRes.data || { groups: [], users: [], roles: [] });
      setNotificationTypes(typesRes.data?.types || {});
    } catch (error) {
      toast.error('Error al cargar datos');
    } finally {
      setLoading(false);
    }
  };

  const handleAddTarget = () => {
    if (!selectorId) return;
    
    // Verificar que no esté duplicado
    const exists = form.targets.some(t => t.type === selectorType && t.id === selectorId);
    if (exists) {
      toast.error('Este destinatario ya está agregado');
      return;
    }
    
    setForm(prev => ({
      ...prev,
      targets: [...prev.targets, { type: selectorType, id: selectorId }]
    }));
    
    setSelectorId('');
  };

  const handleRemoveTarget = (index) => {
    setForm(prev => ({
      ...prev,
      targets: prev.targets.filter((_, i) => i !== index)
    }));
  };

  const handleCreate = async () => {
    if (form.targets.length === 0) {
      toast.error('Debes agregar al menos un destinatario');
      return;
    }
    
    try {
      const res = await api.post('/notifications/v2/subscriptions', form);
      setSubscriptions(prev => [res.data, ...prev]);
      setIsDialogOpen(false);
      resetForm();
      toast.success('Suscripción creada');
    } catch (error) {
      toast.error('Error al crear suscripción');
    }
  };

  const handleUpdate = async () => {
    if (form.targets.length === 0) {
      toast.error('Debes agregar al menos un destinatario');
      return;
    }
    
    try {
      const res = await api.put(`/notifications/v2/subscriptions/${editingSubscription.id}`, form);
      setSubscriptions(prev => prev.map(s => s.id === editingSubscription.id ? res.data : s));
      setIsDialogOpen(false);
      setEditingSubscription(null);
      resetForm();
      toast.success('Suscripción actualizada');
    } catch (error) {
      toast.error('Error al actualizar');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('¿Eliminar esta suscripción?')) return;
    
    try {
      await api.delete(`/notifications/v2/subscriptions/${id}`);
      setSubscriptions(prev => prev.filter(s => s.id !== id));
      toast.success('Suscripción eliminada');
    } catch (error) {
      toast.error('Error al eliminar');
    }
  };

  const handleToggleActive = async (subscription) => {
    try {
      const res = await api.put(`/notifications/v2/subscriptions/${subscription.id}`, {
        active: !subscription.active
      });
      setSubscriptions(prev => prev.map(s => s.id === subscription.id ? res.data : s));
    } catch (error) {
      toast.error('Error al actualizar');
    }
  };

  const openEditDialog = (subscription) => {
    setEditingSubscription(subscription);
    
    // Convertir targets legacy a nuevo formato si es necesario
    let targets = subscription.targets || [];
    if (targets.length === 0 && subscription.target_type && subscription.target_id) {
      targets = [{ type: subscription.target_type, id: subscription.target_id }];
    }
    
    setForm({
      name: subscription.name,
      targets: targets,
      notification_types: subscription.notification_types || ['*'],
      channels: subscription.channels || ['in_app'],
      digest_frequency: subscription.digest_frequency || 'daily',
      digest_day: subscription.digest_day || 'mon',
      digest_hour: subscription.digest_hour || 8,
      priority_threshold: subscription.priority_threshold || 'high',
      active: subscription.active
    });
    setIsDialogOpen(true);
  };

  const resetForm = () => {
    setForm({
      name: '',
      targets: [],
      notification_types: ['*'],
      channels: ['in_app', 'digest'],
      digest_frequency: 'daily',
      digest_day: 'mon',
      digest_hour: 8,
      priority_threshold: 'high',
      active: true
    });
    setSelectorType('group');
    setSelectorId('');
    setEditingSubscription(null);
  };

  const getTargetName = (type, id) => {
    if (type === 'group') {
      const group = availableTargets.groups.find(g => g.id === id);
      return group?.nombre || id;
    }
    if (type === 'user') {
      const user = availableTargets.users.find(u => u.id === id);
      return user?.nombre || user?.email || id;
    }
    if (type === 'role') {
      return `Rol: ${id}`;
    }
    return id;
  };

  const getTargetTypeLabel = (type) => {
    const labels = { group: 'Grupo', user: 'Usuario', role: 'Rol' };
    return labels[type] || type;
  };

  const getSelectorOptions = () => {
    switch (selectorType) {
      case 'group':
        return availableTargets.groups.map(g => ({ value: g.id, label: g.nombre }));
      case 'user':
        return availableTargets.users.map(u => ({ value: u.id, label: u.nombre || u.email }));
      case 'role':
        return availableTargets.roles.map(r => ({ value: r, label: r }));
      default:
        return [];
    }
  };

  const getFrequencyLabel = (freq) => {
    return FREQUENCY_OPTIONS.find(f => f.value === freq)?.label || freq;
  };

  // Obtener todos los targets de una suscripción (nuevo formato o legacy)
  const getSubscriptionTargets = (sub) => {
    if (sub.targets && sub.targets.length > 0) {
      return sub.targets;
    }
    if (sub.target_type && sub.target_id) {
      return [{ type: sub.target_type, id: sub.target_id }];
    }
    return [];
  };

  return (
    <div className="space-y-6 p-6 lg:p-8">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-semibold text-slate-900">Suscripciones de Notificaciones</h3>
          <p className="text-sm text-slate-500">
            Configura quién recibe cada tipo de notificación y cómo
          </p>
        </div>
        <div className="flex gap-2">
          <Button
            variant="outline"
            onClick={async () => {
              try {
                const res = await api.post('/notifications/v2/subscriptions/seed-defaults');
                const data = res.data;
                if (data.created?.length > 0) {
                  toast.success(`${data.created.length} suscripciones creadas`);
                  loadData();
                } else {
                  toast.info('Todas las suscripciones por defecto ya existen');
                }
              } catch (error) {
                toast.error('Error al crear suscripciones');
              }
            }}
            data-testid="seed-defaults-btn"
          >
            <RefreshCw size={16} className="mr-2" />
            Cargar Defaults
          </Button>
          <Dialog open={isDialogOpen} onOpenChange={(open) => {
          setIsDialogOpen(open);
          if (!open) resetForm();
        }}>
          <DialogTrigger asChild>
            <Button style={{ backgroundColor: '#4f46e5' }} data-testid="nueva-suscripcion-btn">
              <Plus size={16} className="mr-2" />
              Nueva Suscripción
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>
                {editingSubscription ? 'Editar Suscripción' : 'Nueva Suscripción'}
              </DialogTitle>
              <DialogDescription>
                Define quién recibe las notificaciones y cómo
              </DialogDescription>
            </DialogHeader>
            
            <div className="space-y-4 py-4">
              {/* Nombre */}
              <div>
                <Label>Nombre de la suscripción</Label>
                <Input
                  value={form.name}
                  onChange={(e) => setForm(prev => ({ ...prev, name: e.target.value }))}
                  placeholder="Ej: Equipo de Compliance - Alertas"
                  className="mt-1"
                  style={{ backgroundColor: '#fff' }}
                />
              </div>

              {/* Destinatarios - NUEVO: Multi-selección */}
              <div>
                <Label className="mb-2 block">Destinatarios</Label>
                
                {/* Lista de destinatarios agregados */}
                <div className="flex flex-wrap gap-2 mb-3">
                  {form.targets.map((target, idx) => {
                    const TargetIcon = TARGET_ICONS[target.type] || Users;
                    const colorClass = TARGET_COLORS[target.type] || 'bg-slate-100 text-slate-700';
                    
                    return (
                      <Badge
                        key={`${target.type}-${target.id}`}
                        variant="outline"
                        className={`${colorClass} flex items-center gap-1 pr-1`}
                      >
                        <TargetIcon size={12} />
                        <span className="text-xs font-normal mr-1">
                          {getTargetTypeLabel(target.type)}:
                        </span>
                        <span className="text-xs font-medium">
                          {getTargetName(target.type, target.id)}
                        </span>
                        <Button
                          variant="ghost"
                          size="sm"
                          className="h-4 w-4 p-0 hover:bg-red-100 rounded-full"
                          onClick={() => handleRemoveTarget(idx)}
                        >
                          <X size={10} />
                        </Button>
                      </Badge>
                    );
                  })}
                  {form.targets.length === 0 && (
                    <span className="text-sm text-slate-400">Sin destinatarios</span>
                  )}
                </div>
                
                {/* Selector para agregar */}
                <div className="flex gap-2">
                  <Select
                    value={selectorType}
                    onValueChange={(v) => { setSelectorType(v); setSelectorId(''); }}
                  >
                    <SelectTrigger className="w-28" style={{ backgroundColor: '#fff' }}>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="group">Grupo</SelectItem>
                      <SelectItem value="user">Usuario</SelectItem>
                      <SelectItem value="role">Rol</SelectItem>
                    </SelectContent>
                  </Select>
                  
                  <Select
                    value={selectorId}
                    onValueChange={setSelectorId}
                  >
                    <SelectTrigger className="flex-1" style={{ backgroundColor: '#fff' }}>
                      <SelectValue placeholder="Seleccionar..." />
                    </SelectTrigger>
                    <SelectContent>
                      {getSelectorOptions().map(opt => (
                        <SelectItem key={opt.value} value={opt.value}>
                          {opt.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={handleAddTarget}
                    disabled={!selectorId}
                  >
                    <Plus size={14} />
                  </Button>
                </div>
              </div>

              {/* Tipos de notificación */}
              <div>
                <Label>Tipos de notificación</Label>
                <Select
                  value={form.notification_types[0]}
                  onValueChange={(v) => setForm(prev => ({ ...prev, notification_types: [v] }))}
                >
                  <SelectTrigger className="mt-1" style={{ backgroundColor: '#fff' }}>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="max-h-80 overflow-y-auto">
                    <SelectItem value="*">Todos los tipos</SelectItem>
                    {/* Agrupar por categoría */}
                    {Object.entries(
                      Object.entries(notificationTypes).reduce((acc, [id, info]) => {
                        const cat = info.category || 'otros';
                        if (!acc[cat]) acc[cat] = [];
                        acc[cat].push({ id, ...info });
                        return acc;
                      }, {})
                    ).map(([category, types]) => (
                      <React.Fragment key={category}>
                        <div className="px-2 py-1.5 text-xs font-semibold text-slate-500 uppercase bg-slate-100 border-b border-slate-200">
                          {category === 'alertas' ? '⚠️ Alertas' :
                           category === 'casos' ? '📁 Casos' :
                           category === 'escalaciones' ? '🚨 Escalaciones' :
                           category === 'monitores' ? '📊 Monitores' :
                           category === 'aprobaciones' ? '✅ Aprobaciones' :
                           category === 'onboarding' ? '🏢 Onboarding Empresas' :
                           category}
                        </div>
                        {types.map(type => (
                          <SelectItem key={type.id} value={type.id}>
                            {type.name}
                          </SelectItem>
                        ))}
                      </React.Fragment>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Canales */}
              <div>
                <Label className="mb-2 block">Canales</Label>
                <div className="flex flex-wrap gap-3">
                  {['in_app', 'email', 'digest'].map(channel => (
                    <label key={channel} className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={form.channels.includes(channel)}
                        onChange={(e) => {
                          const newChannels = e.target.checked
                            ? [...form.channels, channel]
                            : form.channels.filter(c => c !== channel);
                          setForm(prev => ({ ...prev, channels: newChannels }));
                        }}
                        className="rounded border-slate-300"
                      />
                      <span className="text-sm capitalize">
                        {channel === 'in_app' ? 'In-App' : channel === 'digest' ? 'Digest' : 'Email'}
                      </span>
                    </label>
                  ))}
                </div>
              </div>

              {/* Configuración de Digest */}
              {form.channels.includes('digest') && (
                <div className="p-4 bg-slate-50 rounded-lg space-y-3">
                  <h4 className="font-medium text-sm text-slate-700">Configuración de Digest</h4>
                  
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label className="text-xs">Frecuencia</Label>
                      <Select
                        value={form.digest_frequency}
                        onValueChange={(v) => setForm(prev => ({ ...prev, digest_frequency: v }))}
                      >
                        <SelectTrigger className="mt-1" style={{ backgroundColor: '#fff' }}>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {FREQUENCY_OPTIONS.map(opt => (
                            <SelectItem key={opt.value} value={opt.value}>
                              {opt.label}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    
                    {form.digest_frequency === 'weekly' && (
                      <div>
                        <Label className="text-xs">Día</Label>
                        <Select
                          value={form.digest_day}
                          onValueChange={(v) => setForm(prev => ({ ...prev, digest_day: v }))}
                        >
                          <SelectTrigger className="mt-1" style={{ backgroundColor: '#fff' }}>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            {DAY_OPTIONS.map(opt => (
                              <SelectItem key={opt.value} value={opt.value}>
                                {opt.label}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                    )}
                    
                    <div>
                      <Label className="text-xs">Hora de envío</Label>
                      <Input
                        type="number"
                        min={0}
                        max={23}
                        value={form.digest_hour}
                        onChange={(e) => setForm(prev => ({ ...prev, digest_hour: parseInt(e.target.value) }))}
                        className="mt-1"
                        style={{ backgroundColor: '#fff' }}
                      />
                    </div>
                  </div>
                </div>
              )}

              {/* Prioridad para email inmediato */}
              {form.channels.includes('email') && (
                <div>
                  <Label>Prioridad mínima para email inmediato</Label>
                  <Select
                    value={form.priority_threshold}
                    onValueChange={(v) => setForm(prev => ({ ...prev, priority_threshold: v }))}
                  >
                    <SelectTrigger className="mt-1" style={{ backgroundColor: '#fff' }}>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {PRIORITY_OPTIONS.map(opt => (
                        <SelectItem key={opt.value} value={opt.value}>
                          {opt.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <p className="text-xs text-slate-400 mt-1">
                    Solo se envían emails inmediatos para notificaciones con esta prioridad o mayor
                  </p>
                </div>
              )}
            </div>

            <DialogFooter>
              <Button variant="outline" onClick={() => setIsDialogOpen(false)}>
                Cancelar
              </Button>
              <Button
                onClick={editingSubscription ? handleUpdate : handleCreate}
                style={{ backgroundColor: '#4f46e5' }}
                disabled={!form.name || form.targets.length === 0}
              >
                {editingSubscription ? 'Guardar' : 'Crear'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
        </div>
      </div>

      {/* Lista de Suscripciones */}
      {loading ? (
        <div className="flex items-center justify-center py-12">
          <RefreshCw className="animate-spin text-slate-400" size={24} />
        </div>
      ) : subscriptions.length === 0 ? (
        <Card className="bg-white">
          <CardContent className="py-12 text-center">
            <Users size={48} className="mx-auto mb-4 text-slate-300" />
            <p className="text-slate-500">No hay suscripciones configuradas</p>
            <p className="text-sm text-slate-400 mt-1">
              Crea una suscripción para definir quién recibe las notificaciones
            </p>
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {subscriptions.map((sub) => {
            const targets = getSubscriptionTargets(sub);
            
            return (
              <Card key={sub.id} className={`bg-white ${!sub.active ? 'opacity-60' : ''}`}>
                <CardContent className="p-4">
                  <div className="flex items-start justify-between mb-3">
                    <div className="flex-1">
                      <h4 className="font-medium text-slate-900">{sub.name}</h4>
                      
                      {/* Lista de targets */}
                      <div className="flex flex-wrap gap-1 mt-2">
                        {targets.map((target, idx) => {
                          const TargetIcon = TARGET_ICONS[target.type] || Users;
                          const colorClass = TARGET_COLORS[target.type] || 'bg-slate-100 text-slate-700';
                          
                          return (
                            <Badge
                              key={idx}
                              variant="outline"
                              className={`${colorClass} text-xs`}
                            >
                              <TargetIcon size={10} className="mr-1" />
                              {getTargetName(target.type, target.id)}
                            </Badge>
                          );
                        })}
                      </div>
                    </div>
                    <Switch
                      checked={sub.active}
                      onCheckedChange={() => handleToggleActive(sub)}
                    />
                  </div>

                  <div className="flex flex-wrap gap-2 mb-3">
                    {sub.channels?.includes('in_app') && (
                      <Badge variant="outline" className="text-xs flex items-center gap-1">
                        <Bell size={10} />
                        In-App
                      </Badge>
                    )}
                    {sub.channels?.includes('email') && (
                      <Badge variant="outline" className="text-xs flex items-center gap-1">
                        <Mail size={10} />
                        Email ({sub.priority_threshold}+)
                      </Badge>
                    )}
                    {sub.channels?.includes('digest') && (
                      <Badge variant="outline" className="text-xs flex items-center gap-1">
                        <FileText size={10} />
                        Digest {getFrequencyLabel(sub.digest_frequency)}
                      </Badge>
                    )}
                  </div>

                  <div className="flex items-center justify-between pt-3 border-t border-slate-100">
                    <div className="text-xs text-slate-400">
                      {sub.notification_types?.[0] === '*' ? 'Todos los tipos' : `${sub.notification_types?.length || 0} tipo(s)`}
                    </div>
                    <div className="flex items-center gap-1">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => openEditDialog(sub)}
                      >
                        <Edit size={14} />
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        className="text-red-500 hover:text-red-600"
                        onClick={() => handleDelete(sub.id)}
                      >
                        <Trash2 size={14} />
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default NotificacionesSuscripciones;
